/**
 * Formata um valor numérico como moeda brasileira (R$)
 * @param {number|string|null}valor - Valor a ser formatado
 * @returns {string} Valor formatado como moeda
 */
export const formatarMoeda = (valor) => {
  if (!valor && valor !== 0) return 'R$0,00';
  const num = typeof valor === 'string' 
    ? parseFloat(valor.replace(/[^\d,.-]/g, '').replace(',', '.')) 
    : valor;
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(num);
};

/**
 * Formata um valor numérico como percentual com 2 casas decimais
 * @param {number|string|null} valor - Valor a ser formatado
 * @returns {string} Valor formatado como percentual (com '-' se negativo)
 */
export const formatarPercentual = (valor) => {
  if (!valor && valor !== 0) return '0%';
  const num = typeof valor === 'string' 
    ? parseFloat(valor.replace(/[^\d,.-]/g, '').replace(',', '.')) 
    : valor;
  const absValor = Math.abs(num).toFixed(2);
  return `${num < 0 ? '-' : ''}${absValor}%`;
};

/**
 * Formata um valor numérico como litros (com separador de milhares e "L" no final)
 * @param {number|string|null} valor - Valor a ser formatado
 * @returns {string} Valor formatado como litros (ex: "100.160.323.023L")
 */
export const formatarLitros = (valor) => {
  if (!valor && valor !== 0) return '0L';
  const num = typeof valor === 'string' 
    ? parseFloat(valor.replace(/[^\d,.-]/g, '').replace(',', '.')) 
    : valor;
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  }).format(num) + ' L';
};

/**
 * Formata um valor numérico como litros (com separador de milhares e "L" no final)
 * @param {number|string|null} valor - Valor a ser formatado
 * @returns {string} Valor formatado como litros (ex: "100.160.323.023L")
 */
export const formatarQuantidade = (valor) => {
  if (!valor && valor !== 0) return '0';
  const num = typeof valor === 'string' 
    ? parseFloat(valor.replace(/[^\d,.-]/g, '').replace(',', '.')) 
    : valor;
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
};
/**
 * Formata um valor numérico de forma abreviada (K, M)
 * @param {number|string|null} valor - Valor a ser formatado
 * @param {string} indicador - Tipo de indicador ('litros', 'lucro', 'faturamento')
 * @param {string} tipo - Tipo de formatação ('abreviado' ou 'tooltip')
 * @returns {string} Valor formatado de forma abreviada
 */
export const formatarValorAbreviado = (valor, indicador = 'faturamento', tipo = 'abreviado') => {
  if (valor === null || valor === undefined) return 'Sem dado';
  const isLitros = indicador === 'litros';
  if (tipo === 'abreviado') {
    if (isLitros) {
      if (valor >= 1000000) return (valor / 1000000).toFixed(1) + 'M L';
      if (valor >= 1000) return (valor / 1000).toFixed(1) + 'K L';
      return valor.toFixed(0) + ' L';
    }
    if (valor >= 1000000) return (valor / 1000000).toFixed(1) + 'M';
    if (valor >= 1000) return (valor / 1000).toFixed(1) + 'K';
    return valor.toFixed(0);
  }
  if (isLitros) {
    if (valor >= 1000000) return (valor / 1000000).toFixed(1) + 'M L';
    if (valor >= 1000) return (valor / 1000).toFixed(1) + 'K L';
    return valor.toFixed(0) + ' L';
  }
  if (valor >= 1000000) return (valor / 1000000).toFixed(1) + 'M';
  if (valor >= 1000) return (valor / 1000).toFixed(1) + 'K';
  return valor.toFixed(0);
};

/**
 * Formata um valor monetário de forma abreviada com R$ (K, M)
 * @param {number|string|null} valor - Valor a ser formatado
 * @returns {string} Valor formatado como moeda abreviada (ex: "R$ 1.2M", "R$ 500K")
 */
export const formatarMoedaAbreviada = (valor) => {
  if (valor === null || valor === undefined) return 'R$ 0';
  if (valor >= 1000000) return 'R$ ' + (valor / 1000000).toFixed(1) + 'M';
  if (valor >= 1000) return 'R$ ' + (valor / 1000).toFixed(1) + 'K';
  return formatarMoeda(valor);
};

export const MESES_ORDEM = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
export const MESES_ABREVIADOS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
const MESES_ABREVIADOS_MAP = Object.fromEntries(MESES_ORDEM.map((mes, i) => [mes, MESES_ABREVIADOS[i]]));

export const mesAbreviadoParaNumero = (mes) => {
  const index = MESES_ABREVIADOS.indexOf(mes);
  return index !== -1 ? String(index + 1).padStart(2, '0') : null;
};

export const obterMesAbreviado = (mesCompleto) => MESES_ABREVIADOS_MAP[mesCompleto] || mesCompleto;

